import React, { useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const ComboBox = ({
  label,
  required = false,
  value,
  onChange,
  onKeyUp,
  onKeyDown,
  onFocus,
  onBlur,
  suggestions = [],
  isLoading = false,
  onSuggestionClick,
  showSuggestions = false,
  placeholder,
  error,
  className = '',
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus && onFocus(e);
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      setIsFocused(false);
      onBlur && onBlur(e);
    }, 200);
  };

  const wrapperClasses = useMemo(() => {
    const base = 'relative space-y-2 rounded-lg bg-slate-50 border transition-all duration-200 p-3 cursor-text';

    if (error) {
      return `${base} ring-1 ${isFocused ? 'ring-red-400 border-red-400' : 'ring-red-300 border-red-300'} hover:border-red-400 hover:ring-red-400`;
    }

    if (isFocused) {
      return `${base} border-blue-400 ring-1`;
    }

    return `${base} border-slate-200 hover:border-gray-400`;
  }, [error, isFocused]);

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className={wrapperClasses}>
        <label className={`
          block text-xs uppercase font-bold transition-all duration-200
          ${error ? 'text-red-500' : 'text-slate-500'}
        `}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={onChange}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            className="bg-slate-50 text-sm w-full placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed"
            {...props}
          />
          
          {isLoading && (
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <ul
            ref={dropdownRef}
            className="absolute left-0 z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
            style={{ marginTop: '4px' }}
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSuggestionClick(suggestion)}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm border-b border-gray-100 last:border-b-0"
              >
                {`${suggestion.streetLine}${suggestion.secondary ? ` ${suggestion.secondary},` : ''} ${suggestion.city}, ${suggestion.state}, ${suggestion.zipcode}`}
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {error && error !== 'The input field is required' && <p className="text-xs text-red-500 px-2">{error}</p>}
    </div>
  );
};

ComboBox.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  suggestions: PropTypes.array,
  isLoading: PropTypes.bool,
  onSuggestionClick: PropTypes.func.isRequired,
  showSuggestions: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  className: PropTypes.string,
};

export default ComboBox;